import type { IconName } from "@otrack/ui";

export type PaletteGroup = "Command bar" | "Timer" | "Actions" | "Go to" | "Projects" | "Clients";

export interface PaletteItem {
  id: string;
  group: PaletteGroup;
  label: string;
  /** Secondary text after the label, like a Project's Client. */
  meta?: string | undefined;
  /** Extra words that should find this item without being shown. */
  keywords?: string | undefined;
  icon: IconName;
  shortcut?: string | undefined;
  /** Hidden until the User types something. */
  searchOnly?: boolean | undefined;
  run: () => void;
}

export interface PaletteSection { group: PaletteGroup; items: PaletteItem[] }

const GROUPS: PaletteGroup[] = ["Timer", "Actions", "Go to", "Projects", "Clients"];

/** "⌘" on Apple devices, "Ctrl" elsewhere. */
export function modifierKey(): string {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";
}

function normalize(text: string): string {
  return text.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
}

/**
 * How well an item matches the query: every query word must start a word of the label, meta, or
 * keywords (or appear inside the label). 0 means no match.
 */
export function matchScore(query: string, item: Pick<PaletteItem, "label" | "meta" | "keywords">): number {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return 1;
  const label = normalize(item.label);
  const labelWords = label.split(/[^a-z0-9]+/).filter(Boolean);
  const otherWords = normalize(`${item.meta ?? ""} ${item.keywords ?? ""}`).split(/[^a-z0-9]+/).filter(Boolean);
  let score = 0;
  for (const term of terms) {
    if (labelWords.some((word) => word.startsWith(term))) score += 3;
    else if (otherWords.some((word) => word.startsWith(term))) score += 2;
    else if (label.includes(term)) score += 1;
    else return 0;
  }
  if (label.startsWith(normalize(query).trim())) score += 2;
  return score;
}

/**
 * Items grouped for display; "Command bar" items are left for the caller to place. Without a
 * query, groups keep their order and search-only items stay hidden; with one, groups are ordered
 * by their best match and items within them by score.
 */
export function rankPalette(items: PaletteItem[], query: string, limitPerGroup = 6): PaletteSection[] {
  const searching = query.trim().length > 0;
  const scored = items
    .map((item, index) => ({ item, index, score: searching ? matchScore(query, item) : item.searchOnly ? 0 : 1 }))
    .filter((entry) => entry.score > 0);
  const sections = GROUPS.map((group) => {
    const entries = scored.filter((entry) => entry.item.group === group).sort((a, b) => b.score - a.score || a.index - b.index);
    return { group, best: entries[0]?.score ?? 0, items: entries.slice(0, limitPerGroup).map((entry) => entry.item) };
  }).filter((section) => section.items.length > 0);
  if (searching) sections.sort((a, b) => b.best - a.best || GROUPS.indexOf(a.group) - GROUPS.indexOf(b.group));
  return sections.map(({ group, items: sectionItems }) => ({ group, items: sectionItems }));
}
